import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { useTranslation } from 'react-i18next';
import { Clock, CheckCircle2, XCircle, Loader2, ArrowUpRight, HelpCircle } from 'lucide-react';
import { formatPrice, formatDate, convertFromEur } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import type { Payout, PayoutStatus } from '../types/payout.types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/molecules/alert-dialog';

type StatusConfigEntry = {
  icon: React.ElementType;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  color: string;
};

// Miroir exact de `PayoutStatus` (pay-v2). Doc 55, revue finale Finding 1 :
// l'ancien enum Stripe-like (PENDING/PROCESSING/SUCCEEDED/CANCELLED) n'était
// jamais émis par le serveur réel — un payout REQUESTED faisait planter tout
// l'écran vendeur (`config` undefined). Ne JAMAIS indexer sans fallback.
const statusConfig: Record<PayoutStatus, StatusConfigEntry> = {
  REQUESTED: { icon: Clock, variant: 'outline', color: 'text-yellow-600 bg-yellow-100' },
  PENDING_APPROVAL: { icon: Clock, variant: 'outline', color: 'text-amber-600 bg-amber-100' },
  WAITING_HOLD: { icon: Clock, variant: 'outline', color: 'text-yellow-600 bg-yellow-100' },
  EXECUTING: { icon: Loader2, variant: 'secondary', color: 'text-blue-600 bg-blue-100' },
  COMPLETED: { icon: CheckCircle2, variant: 'default', color: 'text-green-600 bg-green-100' },
  FAILED: { icon: XCircle, variant: 'destructive', color: 'text-red-600 bg-red-100' },
  CANCELED: { icon: XCircle, variant: 'secondary', color: 'text-gray-600 bg-gray-100' },
};

// Filet de sécurité : un statut inconnu (pay-v2 ajoute une valeur avant que
// le SDK/front ne soit mis à jour) ne doit plus jamais faire disparaître
// l'écran — cf. RED de payouts-list.test.tsx.
const FALLBACK_STATUS_CONFIG: StatusConfigEntry = {
  icon: HelpCircle,
  variant: 'outline',
  color: 'text-gray-600 bg-gray-100',
};

// Statuts depuis lesquels pay-v2 autorise une transition vers CANCELED
// (`payout-state-machine.helper.ts`, pay-serveur-v2). EXECUTING/COMPLETED/
// FAILED/CANCELED sont terminaux ou déjà en vol chez le PSP — pas annulables.
const CANCELLABLE_STATUSES: ReadonlySet<PayoutStatus> = new Set([
  'REQUESTED',
  'PENDING_APPROVAL',
  'WAITING_HOLD',
]);

export interface PayoutsListProps {
  payouts: Payout[];
  isLoading: boolean;
  isError: boolean;
  onRetry?: () => void;
  onCancel?: (id: string) => void | Promise<void>;
}

export function PayoutsList({ payouts, isLoading, isError, onRetry, onCancel }: PayoutsListProps) {
  const { t } = useTranslation();
  const { currency } = useConfigStore();

  const handleCancel = async (id: string) => {
    try {
      await onCancel?.(id);
    } catch (error) {
      console.error('Failed to cancel payout:', error);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('payouts.history.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ArrowUpRight className="size-5" />
          {t('payouts.history.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isError ? (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{t('payouts.history.error')}</AlertDescription>
            </Alert>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                {t('common.retry')}
              </Button>
            )}
          </div>
        ) : payouts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <ArrowUpRight className="size-12 mb-4 opacity-50" />
            <p>{t('payouts.history.empty')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {payouts.map((payout) => {
              const config = statusConfig[payout.status] ?? FALLBACK_STATUS_CONFIG;
              const StatusIcon = config.icon;

              return (
                <div
                  key={payout.id}
                  className="flex items-center justify-between py-4 border-b last:border-0"
                >
                  <div className="flex items-center gap-4">
                    <div className={`rounded-full p-2 ${config.color}`}>
                      <StatusIcon className={`size-4 ${payout.status === 'EXECUTING' ? 'animate-spin' : ''}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {formatPrice(convertFromEur(payout.amount, currency), currency)}
                        </span>
                        <Badge variant={config.variant}>
                          {t(`payouts.status.${payout.status}`)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{formatDate(payout.createdAt)}</span>
                      </div>
                      {payout.description && (
                        <p className="text-xs text-muted-foreground mt-1">{payout.description}</p>
                      )}
                      {payout.errorMessage && (
                        <p className="text-xs text-destructive mt-1">{payout.errorMessage}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {CANCELLABLE_STATUSES.has(payout.status) && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive">
                            {t('common.cancel')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('payouts.cancel.title')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('payouts.cancel.description')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.no')}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleCancel(payout.id)}
                              className="bg-destructive text-destructive-foreground"
                            >
                              {t('common.yes')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
